import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import PaperBale from './PaperBale';
import { useMagnetic } from '../hooks/useMagnetic';
import Bunting from './Bunting';
import { CURRENT_SEASON } from '../data/season';

// Los fotogramas del hero se sirven desde /hero, no desde Supabase.
//
// Dos motivos medidos, no de estilo:
// 1. Supabase devolvía estas imágenes con `cache-control: no-cache` y
//    `cf-cache-status: MISS`. Ni el CDN ni el navegador las guardaban, así
//    que CADA visita volvía a bajar el hero entero. Desde nuestro origen
//    salen con caché inmutable de un año.
// 2. Eran de 1400 px para un hueco que en móvil mide ~380 px. Reencodadas a
//    1000 px (suficiente para pantallas retina) pasaron de 199 KB a 77 KB
//    entre las cinco.
// Además desaparece un tercer origen del camino crítico: la conexión a
// carpepel.com ya está abierta cuando el parser llega al <img>.
const HERO_FRAMES = '/hero';

const HOLD_MS = 3400;   // cuánto se sostiene cada fotograma
// El cruce se mantiene corto a propósito: el rótulo del HUD cambia de golpe,
// así que un fundido largo deja una ventana en la que la etiqueta (y el
// gramaje) no corresponden al papel que se está viendo. A 520ms el
// solapamiento es imperceptible y el dato nunca miente.
const FADE_MS = 520;

/**
 * Secuencia del hero: fotos REALES del catálogo, normalizadas a un mismo
 * encuadre y con el fondo fundido al tono papel del sitio, de modo que el
 * pliego flota en la página en vez de vivir dentro de una caja blanca.
 * Al encadenarlas con cross-fade + deriva lenta dan la impresión de video
 * pesando 77 KB en total (cinco WebP a 1000 px), sin video ni 3D.
 */
const SEQ = [
    { name: 'Canela', match: 'canela', color: 'var(--stock-canela)' },
    { name: 'Mostaza', match: 'mostaza', color: 'var(--stock-mostaza)' },
    { name: 'Oro', match: 'oro', color: 'var(--stock-oro)' },
    { name: 'Amarillo', match: 'amarillo', color: 'var(--stock-amarillo)' },
    { name: 'Verde', match: 'verde', color: 'var(--stock-verde)' },
];

/**
 * Titular cinético: cada palabra es una máscara (overflow:hidden) y cada
 * carácter dentro entra desde abajo con un delay creciente — arquetipo
 * "cascada" (ver web-design-awwwards §2.4.0), cero dependencias, solo CSS.
 * El texto real vive completo en el DOM (SEO/lectores de pantalla intactos);
 * los spans son puramente visuales.
 */
function KineticHeadline({ lines, accent, startDelay = 0.05, step = 0.022 }) {
    let i = 0;
    return (
        <span>
            {/* El nombre accesible va en un span oculto a la vista, NO en un
                aria-label sobre este contenedor: aria-label solo aplica a
                elementos con rol semántico, y en un <span> pelado los lectores
                de pantalla lo ignoran — Lighthouse lo marca como "prohibited
                ARIA attribute". Con el aria-label ignorado y todo el interior
                aria-hidden, el H1 de la portada se anunciaba VACÍO. */}
            <span className="sr-only">{lines.join(' ')}</span>
            {lines.map((line, li) => (
                <span key={li} className="block" aria-hidden="true">
                    {line.split(' ').map((word, wi, arr) => {
                        const isAccent = word.replace(/[.,]/g, '') === accent;
                        return (
                            <span key={wi}>
                                <span
                                    className="kinetic-word"
                                    style={isAccent ? { color: 'var(--kraft-deep)' } : undefined}
                                >
                                    {[...word].map((ch, ci) => (
                                        <span
                                            key={ci}
                                            className="kinetic-char"
                                            style={{ '--kd': `${startDelay + (i++) * step}s` }}
                                        >
                                            {ch}
                                        </span>
                                    ))}
                                </span>
                                {wi < arr.length - 1 ? ' ' : ''}
                            </span>
                        );
                    })}
                </span>
            ))}
        </span>
    );
}

function usePrefersReducedMotion() {
    const [reduce, setReduce] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const apply = () => setReduce(mq.matches);
        apply();
        mq.addEventListener('change', apply);
        return () => mq.removeEventListener('change', apply);
    }, []);
    return reduce;
}

export default function Hero({ products = [] }) {
    const [index, setIndex] = useState(0);
    const catalogCtaRef = useMagnetic(0.25, 8);
    // El rótulo (nombre + gramaje + medida) describe un producto concreto, así
    // que NUNCA debe estar visible mientras la imagen está a medio fundir: se
    // leería un gramaje que no corresponde al papel en pantalla. En vez de
    // pelear por sincronizar temporizadores con las transiciones CSS —que se
    // desfasan cuando el navegador estrangula los timers, medido— el HUD se
    // apaga durante el cruce y solo reaparece con el dato ya correcto.
    const [labelIndex, setLabelIndex] = useState(0);
    const [hudOn, setHudOn] = useState(true);
    const [paused, setPaused] = useState(false);
    const reduce = usePrefersReducedMotion();

    useEffect(() => {
        if (reduce) { setLabelIndex(index); setHudOn(true); return undefined; }
        setHudOn(false);
        const t = setTimeout(() => { setLabelIndex(index); setHudOn(true); }, FADE_MS);
        return () => clearTimeout(t);
    }, [index, reduce]);

    // Con motion reducido la secuencia no avanza sola: queda el primer
    // fotograma fijo y el usuario navega con la franja.
    useEffect(() => {
        // Sin autoplay al pre-renderizar: el HTML estático debe congelar
        // siempre el primer fotograma, no el que tocara al tomar el snapshot.
        if (reduce || paused || (typeof window !== 'undefined' && window.__PRERENDER__)) return undefined;
        const id = setInterval(() => setIndex((v) => (v + 1) % SEQ.length), HOLD_MS);
        return () => clearInterval(id);
    }, [reduce, paused]);

    // Los cuatro fotogramas siguientes NO se montan de entrada.
    //
    // Llevaban loading="lazy", que aquí no servía de nada: están dentro del
    // viewport (superpuestos al primero con opacity:0), y `lazy` solo aplaza
    // lo que está FUERA de pantalla. Medido en producción: los cinco
    // arrancaban con 260 ms de diferencia y terminaban a la vez, o sea que
    // 128 KB de imágenes que nadie ve todavía competían por el ancho de
    // banda con la imagen LCP de la portada — la que decide la sensación de
    // velocidad. El LCP se iba a 6.8 s y el 63% era descarga.
    //
    // Ahora se montan cuando el primero ya cargó (o al segundo, por si su
    // `load` no llega: caché, error de red). Sobra tiempo: el primer cruce
    // no ocurre hasta HOLD_MS, muy por encima de ese margen.
    //
    // Durante el pre-renderizado NO se montan nunca. Antes, el snapshot se
    // tomaba cuando el primero ya había cargado, así que el HTML estático
    // traía los cinco <img> y el navegador del visitante descargaba los cinco
    // de entrada — antes de que el JS pudiera aplazar nada —, compitiendo con
    // la imagen LCP. Ahora el HTML solo trae la primera; las demás las monta
    // el navegador como estaba previsto.
    const isPrerender = typeof window !== 'undefined' && window.__PRERENDER__;
    const [mountRest, setMountRestState] = useState(false);
    const setMountRest = useCallback((v) => { if (!isPrerender) setMountRestState(v); }, [isPrerender]);
    useEffect(() => {
        if (mountRest || isPrerender) return undefined;
        const t = setTimeout(() => setMountRest(true), 2000);
        return () => clearTimeout(t);
    }, [mountRest, isPrerender, setMountRest]);

    const productFor = useCallback(
        (match) => products.find((p) => p.name?.toLowerCase().includes(match)),
        [products],
    );

    // `active` = lo que el usuario está viendo realmente (rótulo, specs y
    // destino del enlace); `index` solo manda sobre qué imagen se funde.
    const active = SEQ[labelIndex];
    const activeProduct = productFor(active.match);
    const hrefFor = (match) => {
        const p = productFor(match);
        return p ? `/producto/${p.id}` : '/catalogo';
    };

    return (
        <section
            data-testid="hero-section"
            className="relative"
            style={{ '--hero-hold': `${HOLD_MS}ms` }}
            /* El pausado cubre TODA la sección, franja incluida: si el usuario
               está explorando un color con el cursor, el autoplay no debe
               arrebatarle el fotograma que está mirando. */
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div
                className="relative hero-noise overflow-hidden"
                style={{ backgroundColor: 'var(--paper)' }}
            >
                {CURRENT_SEASON?.id === 'navidad' && (
                    <Bunting className="pointer-events-none absolute inset-x-0 top-0 h-[50px] w-full md:h-[86px]" />
                )}
                <PaperBale
                    variant="line"
                    color="var(--kraft-deep)"
                    className="pointer-events-none absolute -left-20 bottom-0 h-[300px] w-[300px] opacity-[0.06] md:h-[400px] md:w-[400px]"
                />

                <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-14 pb-10 md:pt-20 md:pb-14">
                    {/* En móvil el orden del DOM manda: overline+titular, LUEGO la
                        foto, y al final bajada+botones. Con el bloque de texto
                        completo arriba, el producto caía bajo el pliegue y había
                        que hacer scroll para verlo — justo lo contrario de lo que
                        debe hacer este hero. En desktop la rejilla recoloca la
                        imagen a la derecha ocupando las dos filas. */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-8 lg:gap-x-10 lg:items-center">
                        <div className="lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:self-end">
                            <div
                                className="flex items-center gap-4 mb-7 animate-fadeInUp stagger-1"
                                style={{ opacity: 0 }}
                            >
                                <span className="hidden sm:block h-px w-10 flex-shrink-0" style={{ backgroundColor: 'var(--kraft-deep)' }} />
                                <p
                                    className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.22em]"
                                    style={{ color: 'var(--kraft-deep)' }}
                                >
                                    Insumos alimenticios · Distribución nacional
                                </p>
                            </div>

                            <h1
                                className="font-heading font-black tracking-tight"
                                style={{
                                    color: 'var(--ink)',
                                    fontSize: 'clamp(2.9rem, 6.6vw, 5.5rem)',
                                    lineHeight: 0.88,
                                }}
                            >
                                <KineticHeadline
                                    lines={['El papel que envuelve', 'el sabor de México']}
                                    accent="México"
                                />
                            </h1>
                        </div>

                        {/* Secuencia — el producto real, sin marco: el fondo de las
                            fotos es el mismo tono que la página, así que el pliego
                            flota en la composición en vez de estar "dentro" de algo. */}
                        <div className="lg:col-span-7 lg:col-start-6 lg:row-start-1 lg:row-span-2">
                            <Link
                                to={hrefFor(active.match)}
                                data-testid="hero-sequence"
                                /* Sin aria-label a propósito: el enlace ENVUELVE al
                                   rótulo visible (nombre, gramaje y medida), y un
                                   aria-label que no contiene ese texto viola el
                                   criterio 2.5.3 de WCAG — quien navega por voz dice
                                   lo que ve ("Canela") y no activa nada. Dejando que
                                   el nombre accesible salga del propio contenido,
                                   lo que se oye y lo que se ve son lo mismo. */
                                className="group relative block aspect-[3/2] w-full"
                            >
                                {SEQ.map((s, i) => {
                                    // Hasta que el primero carga, los demás no
                                    // existen en el DOM y por tanto no se piden.
                                    if (i > 0 && !mountRest) return null;
                                    return (
                                    <img
                                        key={s.match}
                                        src={`${HERO_FRAMES}/seq_${s.match}.webp`}
                                        onLoad={i === 0 ? () => setMountRest(true) : undefined}
                                        alt={
                                            i === index
                                                ? `Papel ${s.name} grado alimenticio: resma con un pliego levantado`
                                                : ''
                                        }
                                        aria-hidden={i !== index}
                                        width="1000"
                                        height="666"
                                        /* `eager` en TODOS, no solo en el primero.
                                           El aplazamiento ya lo hace `mountRest`: si
                                           un fotograma está montado es porque lo
                                           queremos cargado antes de que le toque el
                                           turno. Dejarles además `loading="lazy"`
                                           añadía una segunda condición —estar en
                                           pantalla— que puede no cumplirse nunca
                                           (se vio con un viewport de 0x0: los cuatro
                                           quedaban sin pedir y el cruce llegaba a una
                                           imagen vacía). Una sola puerta, no dos. */
                                        loading="eager"
                                        fetchpriority={i === 0 ? 'high' : undefined}
                                        decoding="async"
                                        className={`absolute inset-0 h-full w-full object-contain ${i === index ? 'hero-drift' : ''}`}
                                        style={{
                                            opacity: i === index ? 1 : 0,
                                            transition: `opacity ${FADE_MS}ms cubic-bezier(0.16,1,0.3,1)`,
                                        }}
                                    />
                                    );
                                })}

                                {/* HUD técnico: los datos reales del producto en pantalla.
                                    Se apaga durante el cruce para no describir un papel
                                    que ya no es el que se está viendo. */}
                                <div
                                    className="absolute left-0 bottom-0 flex items-end gap-4 font-mono"
                                    aria-live="polite"
                                    style={{
                                        opacity: hudOn ? 1 : 0,
                                        transition: 'opacity 200ms ease-out',
                                    }}
                                >
                                    <span
                                        className="text-[11px] tabular-nums"
                                        style={{ color: 'var(--kraft-deep)' }}
                                    >
                                        {String(labelIndex + 1).padStart(2, '0')}/{String(SEQ.length).padStart(2, '0')}
                                    </span>
                                    <span className="leading-tight">
                                        <span
                                            className="block text-[13px] uppercase tracking-[0.16em] font-bold"
                                            style={{ color: 'var(--ink)' }}
                                        >
                                            {active.name}
                                        </span>
                                        {activeProduct && (
                                            <span
                                                className="block text-[11px] tracking-wide"
                                                style={{ color: 'var(--ink-soft)' }}
                                            >
                                                {activeProduct.weight_grams > 0 ? `${activeProduct.weight_grams}g · ` : ''}{activeProduct.sizes?.[0]} · {activeProduct.package_weight_kg}kg
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </Link>
                        </div>

                        <div className="lg:col-span-5 lg:col-start-1 lg:row-start-2 lg:self-start">
                            <p
                                className="mt-7 text-[16px] sm:text-[17px] leading-[1.65] text-pretty max-w-md animate-fadeInUp stagger-3"
                                style={{ color: 'var(--ink-soft)', opacity: 0 }}
                            >
                                Papel grado alimenticio al mayoreo y menudeo, para negocios
                                que no pueden fallar. Compra en minutos, sin crear cuenta.
                            </p>

                            <div
                                className="mt-8 flex flex-wrap items-center gap-3 animate-fadeInUp stagger-4"
                                style={{ opacity: 0 }}
                            >
                                <Link
                                    ref={catalogCtaRef}
                                    to="/catalogo"
                                    data-testid="hero-cta-catalog"
                                    className="magnetic group inline-flex items-center gap-2.5 h-[52px] px-8 rounded-sm uppercase font-bold tracking-widest text-[13px] hover:opacity-85"
                                    style={{ backgroundColor: 'var(--ink)', color: 'var(--paper)' }}
                                >
                                    Comprar ahora
                                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                            </div>

                            {/* Un solo botón: antes "Ver guías" competía con la
                                compra en el primer pliegue. Las guías siguen más
                                abajo y en el menú. Esta línea responde, sin otro
                                clic, las tres dudas que frenan una primera compra. */}
                            <ul
                                className="mt-5 flex flex-wrap gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-[0.14em] animate-fadeInUp stagger-4"
                                style={{ color: 'var(--ink-soft)', opacity: 0 }}
                                data-testid="hero-trust"
                            >
                                {['Sin crear cuenta', 'Pago seguro', 'Envío a todo México'].map((t) => (
                                    <li key={t} className="flex items-center gap-1.5">
                                        <Check className="w-3.5 h-3.5" strokeWidth={2} style={{ color: 'var(--success)' }} />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </div>

                    </div>
                </div>
            </div>

            {/* Línea de tiempo: los colores reales del stock hacen de scrubber.
                Hover previsualiza el fotograma, clic navega al producto. */}
            <div
                className="grid grid-cols-5 border-y"
                style={{ borderColor: 'var(--line)' }}
                data-testid="hero-stock-band"
            >
                {SEQ.map((s, i) => (
                    <Link
                        key={s.name}
                        to={hrefFor(s.match)}
                        data-testid={`hero-stock-${s.match}`}
                        aria-label={`Ver papel ${s.name}`}
                        aria-current={i === index ? 'true' : undefined}
                        onMouseEnter={() => setIndex(i)}
                        onFocus={() => setIndex(i)}
                        className="group relative flex items-end h-20 sm:h-28 md:h-32 overflow-hidden"
                        style={{ backgroundColor: s.color }}
                    >
                        {/* Barra de progreso del fotograma en curso */}
                        {i === index && !reduce && !paused && (
                            <span
                                key={`${index}-scrub`}
                                aria-hidden="true"
                                className="hero-scrub absolute inset-x-0 top-0 h-[3px]"
                                style={{ backgroundColor: 'var(--ink)', opacity: 0.5 }}
                            />
                        )}
                        {/* El segmento activo se aclara: indica dónde está la secuencia */}
                        <span
                            aria-hidden="true"
                            className="absolute inset-0 transition-opacity duration-500"
                            style={{
                                backgroundColor: 'var(--paper)',
                                opacity: i === index ? 0 : 0.42,
                            }}
                        />
                        <span
                            /* 0.82 y no 0.72: medido sobre la franja kraft (#c4a278), 0.72 da 4.23:1 y AA pide 4.5:1. A 0.82 sube a 5.25:1. */
                            className="relative w-full px-3 pb-3 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-center transition-transform duration-500 ease-out group-hover:-translate-y-1"
                            style={{ color: 'rgba(28,26,21,0.82)' }}
                        >
                            {s.name}
                        </span>
                    </Link>
                ))}
            </div>
        </section>
    );
}
